import { Box, Stack, Paper, Container, Typography, Chip } from '@mui/material';
import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import ManualBookingModal from './components/modals/ManualBookingModal';
import CustomButton from '../../components/CustomButton';
import BookingDetailsModal from './components/modals/BookingDetailsModal';
import Loading from '../../components/Loading';
import useDailySchedules from './hooks/useDailySchedules';
import { DailyScheduleCard, DayHeader } from './components/DailyScheduleCard';
import { ResourceDayBoard } from './components/ResourceDayBoard';
import DaySelector from './components/DaySelector';
import TimeRuler, { TimeRulerCorner } from './components/TimeRuler';
import ScheduleScroller from './components/ScheduleScroller';
import OutsideHoursToggle from './components/OutsideHoursToggle';
import useGetBookings from './api/useGetBookings';
import PendingBookingCard from './components/PendingBookingCard';
import BookingDecisionModal from './components/modals/BookingDecisionModal';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ScheduleSkeleton from './components/ScheduleSkeleton';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useTenant } from '../../contexts/TenantContext';
import { useStaff } from './StaffContext';
import ProviderSelect from './components/ProviderSelect';
import {
  buildGridWindow,
  resolveInitialScrollMinute,
  scrollOffsetForMinute,
  minuteForScrollOffset,
  isSlotInWindow,
  upcomingOutsideHoursGuests,
  bookingStartMinutes,
  nextBookingMinuteOfDay,
  localIsoDate,
  CLASSIC_SLOT_HEIGHT,
  RESOURCE_SLOT_WIDTH,
} from './utils/scheduleUtils';

const sectionBg = {
  primary: `
    radial-gradient(ellipse at 0% 0%, rgba(33, 150, 243, 0.06) 0%, transparent 60%),
    radial-gradient(ellipse at 100% 100%, rgba(255, 152, 0, 0.05) 0%, transparent 60%),
    rgba(255, 255, 255, 0.92)
  `,
  secondary: `
    radial-gradient(ellipse at 50% 50%, rgba(0, 0, 0, 0.015) 0%, transparent 80%),
    rgba(248, 248, 250, 0.95)
  `,
};

/**
 * The schedule section holds at least 80% of the viewport, so the calendar keeps a constant
 * share of the screen. The board inside it is capped lower and scrolls internally — see
 * ScheduleScroller.
 */
const SCHEDULE_SECTION_MIN_HEIGHT = '80vh';

/**
 * One provider's calendar: their pending bookings and their board (docs/MULTI_PROVIDER.md, flows 3
 * and 4). DashboardPage mounts it afresh for another provider, so its paging, scroll and modals
 * start over rather than carrying the last provider's state across.
 */
const ProviderCalendar = ({ providerId }) => {
  const [manualBookingModal, setManualBookingModal] = useState({ open: false, data: null });
  const [bookingDetailsModal, setBookingDetailsModal] = useState({ open: false, bookingId: null });
  const [bookingDecisionModal, setBookingDecisionModal] = useState({ open: false, bookingId: null, actionType: null });
  const [requestedDate, setRequestedDate] = useState(null);
  // Off on every visit: the board opens on business time (DECISIONS.md D24).
  const [showOutsideHours, setShowOutsideHours] = useState(false);

  const boardScrollRef = useRef(null);
  const savedScrollRef = useRef({ left: 0, top: 0 });
  const autoScrolledForRef = useRef(null);
  const pendingRestoreRef = useRef(false);
  const anchorMinuteRef = useRef(null);

  const { isResourceProvider, resourceTypes, isBookingApprovalRequired, dailyBreaks, openingHours } = useTenant();

  const gridWindow = useMemo(
    () => buildGridWindow({ showOutsideHours, dailyBreaks, openingHours }),
    [showOutsideHours, dailyBreaks, openingHours]
  );

  // A tenant flagged as a resource provider but carrying no resource types would render an empty
  // board, so fall back to the column view rather than showing nothing.
  const useResourceBoard = isResourceProvider && resourceTypes.length > 0;

  const {
    fetchedDailySchedules,
    loading,
    loadMoreSchedules,
    reloadSchedules,
  } = useDailySchedules(providerId);

  // A tenant without approval confirms bookings straight to BOOKED, so there is never a
  // pending one to list: the request is not even sent.
  const {
    data: pendingBookings = [],
    isLoading: pendingBookingsLoading,
    error: pendingBookingsError,
  } = useGetBookings('PENDING', { enabled: isBookingApprovalRequired, providerId });

  // Derived rather than state-plus-effect: the board's initial scroll waits on having a day to
  // draw, and settling the default through an effect cost a render in which there was none.
  // It also self-heals when a reload replaces the fetched days.
  const selectedSchedule =
    fetchedDailySchedules.find((schedule) => schedule.date === requestedDate) ??
    fetchedDailySchedules[0];
  const selectedDate = selectedSchedule?.date ?? null;

  // The board only exists once there is a day to draw. `loading` starts out false — the fetch
  // only begins in an effect — so an empty first render must not count as "the board is up",
  // or the initial scroll runs against an empty grid and never runs again.
  const hasBoardContent = useResourceBoard
    ? Boolean(selectedSchedule)
    : fetchedDailySchedules.length > 0;

  /**
   * The grid runs over many hours, so where it opens matters. `resolveInitialScrollMinute` owns
   * the rules — they are tabulated in DECISIONS.md D15 — and this runs once per board type. A
   * reload after a booking restores wherever the admin had scrolled to instead.
   */
  useEffect(() => {
    const element = boardScrollRef.current;
    if (loading || !element || !hasBoardContent) return undefined;

    // Scroll offsets need final layout, which is a frame away from the DOM mutation.
    const frame = requestAnimationFrame(() => {
      if (!boardScrollRef.current) return;
      const target = boardScrollRef.current;

      if (pendingRestoreRef.current) {
        target.scrollLeft = savedScrollRef.current.left;
        target.scrollTop = savedScrollRef.current.top;
        pendingRestoreRef.current = false;
        return;
      }

      const boardKey = useResourceBoard ? 'resource' : 'column';
      if (autoScrolledForRef.current === boardKey) return;

      // The column board anchors on today (the fetched days always start there); the resource
      // board on whichever day is selected.
      const shownSchedule = useResourceBoard ? selectedSchedule : fetchedDailySchedules[0];

      // Only a booking on the grid is a landing point; one in hidden hours is not.
      const onGrid = (schedule) =>
        schedule && {
          ...schedule,
          timeSlots: (schedule.timeSlots || []).filter((slot) => isSlotInWindow(gridWindow, slot)),
        };

      const minute = resolveInitialScrollMinute({
        window: gridWindow,
        dayStartMinutes: bookingStartMinutes(onGrid(shownSchedule)),
        isShownDayToday: shownSchedule?.date === localIsoDate(),
        nextBookingMinute: nextBookingMinuteOfDay(fetchedDailySchedules.map(onGrid)),
        fallBackToNextBooking: !useResourceBoard,
      });

      // The target time lands flush against the sticky ruler / gutter, on whichever axis the
      // board runs. No lead-in: opening "on 12:00" should mean 12:00 is the first thing there.
      if (useResourceBoard) {
        target.scrollLeft = scrollOffsetForMinute(minute, RESOURCE_SLOT_WIDTH, gridWindow);
      } else {
        target.scrollTop = scrollOffsetForMinute(minute, CLASSIC_SLOT_HEIGHT, gridWindow);
      }

      autoScrolledForRef.current = boardKey;
    });

    return () => cancelAnimationFrame(frame);
  }, [
    loading,
    hasBoardContent,
    useResourceBoard,
    gridWindow,
    selectedSchedule,
    fetchedDailySchedules,
  ]);

  // Showing or hiding the hours outside opening time grows or shrinks the grid ahead of whatever
  // the admin is looking at. The time at the board's leading edge is noted before the switch and
  // put back after it, so the board does not jump to other hours.
  const toggleOutsideHours = (event) => {
    const scroller = boardScrollRef.current;
    if (scroller) {
      anchorMinuteRef.current = useResourceBoard
        ? minuteForScrollOffset(scroller.scrollLeft, RESOURCE_SLOT_WIDTH, gridWindow)
        : minuteForScrollOffset(scroller.scrollTop, CLASSIC_SLOT_HEIGHT, gridWindow);
    }
    setShowOutsideHours(event.target.checked);
  };

  // A layout effect, so the new grid is never painted at the old offset.
  useLayoutEffect(() => {
    const scroller = boardScrollRef.current;
    const minute = anchorMinuteRef.current;
    anchorMinuteRef.current = null;
    if (!scroller || minute === null) return;

    if (useResourceBoard) {
      scroller.scrollLeft = scrollOffsetForMinute(minute, RESOURCE_SLOT_WIDTH, gridWindow);
    } else {
      scroller.scrollTop = scrollOffsetForMinute(minute, CLASSIC_SLOT_HEIGHT, gridWindow);
    }
  }, [gridWindow, useResourceBoard]);

  const outsideHoursGuests = upcomingOutsideHoursGuests(fetchedDailySchedules, gridWindow);

  const reloadAndSaveScroll = () => {
    if (boardScrollRef.current) {
      savedScrollRef.current = {
        left: boardScrollRef.current.scrollLeft,
        top: boardScrollRef.current.scrollTop,
      };
      pendingRestoreRef.current = true;
    }
    reloadSchedules();
  };

  const openBookingModal = (date, time) => {
    setManualBookingModal({ open: true, data: { date, time } });
  };

  const openBookingDetailsModal = (bookingId) => {
    setBookingDetailsModal({ open: true, bookingId });
  };

  const handleBookingDecision = (actionType, bookingId) => {
    setBookingDecisionModal({ open: true, bookingId, actionType });
  };

  // The id stays while the card fades out, so it does not collapse to its spinner on the way;
  // the card refetches on every opening anyway.
  const closeBookingDetailsModal = () => {
    setBookingDetailsModal((previous) => ({ ...previous, open: false }));
  };

  // The details card hands its decision to the same confirmation modal, and steps aside for it.
  const handleDecisionFromDetails = (actionType, bookingId) => {
    closeBookingDetailsModal();
    handleBookingDecision(actionType, bookingId);
  };

  const ScrollPanel = ({ children }) => (
    <Paper elevation={0} sx={{ overflowX: 'auto', overflowY: 'hidden', transform: 'scaleY(-1)', py: 3, bgcolor: 'transparent' }}>
      <Box display="flex" px={1} sx={{ transform: 'scaleY(-1)', scrollSnapType: 'x mandatory' }}>
        {children}
      </Box>
    </Paper>
  );

  const renderPendingBookingSectionContent = () => {
    if (!isBookingApprovalRequired) return null;
    if (pendingBookingsLoading) return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <Loading />
      </Box>
    );
    if (pendingBookingsError) return (
      <Typography color="error" align="center" fontSize={14}>
        Hiba történt a jóváhagyásra váró foglalások betöltése során.
      </Typography>
    );
    if (!pendingBookings.length) return null;

    return (
      <Box sx={{
          background: sectionBg.primary,
          boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.06)',
          py: 5,
      }}>
        <Container maxWidth="xl">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} mt={3.5}>
            <Box sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              bgcolor: 'rgba(255, 152, 0, 0.12)',
              border: '1px solid rgba(255, 152, 0, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <HourglassEmptyIcon sx={{ fontSize: 16, color: '#ff9800' }} />
            </Box>
            <Typography variant="h6" fontWeight={700} fontSize={17} color="text.primary" letterSpacing="-0.3px">
              Jóváhagyásra váró foglalások
            </Typography>
            <Chip
              label={pendingBookings.length}
              size="small"
              sx={{
                height: 20,
                fontSize: '11px',
                fontWeight: 700,
                bgcolor: 'rgba(255, 152, 0, 0.15)',
                color: '#e65100',
                border: '1px solid rgba(255, 152, 0, 0.3)',
              }}
            />
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ScrollPanel>
              <Stack spacing={2} direction="row">
                {pendingBookings.map(booking => (
                  <Box key={booking.id} width="340px">
                    <PendingBookingCard booking={booking} onDecision={handleBookingDecision} />
                  </Box>
                ))}
              </Stack>
            </ScrollPanel>
          </Box>
        </Container>
      </Box>
    );
  };

  const renderResourceBoard = () => (
    <Stack spacing={2}>
      <DaySelector
        dailySchedules={fetchedDailySchedules}
        selectedDate={selectedDate}
        onSelect={setRequestedDate}
      />
      {selectedSchedule && (
        <ResourceDayBoard
          scrollRef={boardScrollRef}
          dailySchedule={selectedSchedule}
          resourceTypes={resourceTypes}
          gridWindow={gridWindow}
          onBookedSlotClick={openBookingDetailsModal}
          onFreeSlotClick={openBookingModal}
        />
      )}
    </Stack>
  );

  // The day headers ride in the scroller's header row, so they stay put while the hours scroll;
  // the ruler sticks to the left while the days scroll sideways.
  const renderColumnBoard = () => (
    <ScheduleScroller
      ref={boardScrollRef}
      header={
        <Box sx={{ display: 'flex' }}>
          <TimeRulerCorner />
          {fetchedDailySchedules?.map(dailySchedule => (
            <DayHeader key={dailySchedule.date} dailySchedule={dailySchedule} />
          ))}
        </Box>
      }
    >
      <Box sx={{ display: 'flex' }}>
        <TimeRuler gridWindow={gridWindow} />
        {fetchedDailySchedules?.map(dailySchedule => (
          <DailyScheduleCard
            key={dailySchedule.date}
            dailySchedule={dailySchedule}
            gridWindow={gridWindow}
            onBookedSlotClick={openBookingDetailsModal}
            onFreeSlotClick={openBookingModal}
          />
        ))}
      </Box>
    </ScheduleScroller>
  );

  const pendingBookingSectionContent = renderPendingBookingSectionContent();

  return (
    <>
      {/* Pending Bookings Section */}
      {pendingBookingSectionContent}

      {/* Daily Schedules Section */}
      <Box sx={{
        background: sectionBg.primary,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9)',
        py: 5,
        minHeight: SCHEDULE_SECTION_MIN_HEIGHT,
      }}>
        <Container maxWidth="xl">
          <Stack spacing={2}>
            <Box alignSelf="center">
              <CustomButton
                variant="outlined"
                text="Mutass többet"
                onClick={loadMoreSchedules}
                width="300px"
                arrowIcon={<ArrowForwardIcon />}
              />
            </Box>

            <OutsideHoursToggle
              checked={showOutsideHours}
              onChange={toggleOutsideHours}
              upcomingGuests={outsideHoursGuests}
            />

            {loading ? (
              <ScheduleSkeleton />
            ) : useResourceBoard ? (
              renderResourceBoard()
            ) : (
              renderColumnBoard()
            )}
          </Stack>
        </Container>
      </Box>

      <ManualBookingModal
        show={manualBookingModal.open}
        modalData={manualBookingModal.data}
        handleClose={() => setManualBookingModal({ open: false, data: null })}
        reloadSignal={reloadAndSaveScroll}
      />
      <BookingDetailsModal
        show={bookingDetailsModal.open}
        bookingId={bookingDetailsModal.bookingId}
        handleClose={closeBookingDetailsModal}
        reloadSignal={reloadAndSaveScroll}
        onDecision={handleDecisionFromDetails}
      />
      <BookingDecisionModal
        show={bookingDecisionModal.open}
        bookingId={bookingDecisionModal.bookingId}
        actionType={bookingDecisionModal.actionType}
        handleClose={() => setBookingDecisionModal({ open: false, bookingId: null, actionType: null })}
        onDecided={reloadAndSaveScroll}
      />
    </>
  );
};

/**
 * The calendar page. An admin of a tenant with several providers picks whose calendar it shows; it
 * opens on the user's own (docs/MULTI_PROVIDER.md, flow 4).
 */
const DashboardPage = () => {
  const { canChooseProvider, shownProviderId } = useStaff();

  return (
    <>
      {canChooseProvider && (
        <Box sx={{ background: sectionBg.primary, pt: 4, pb: 3 }}>
          <ProviderSelect />
        </Box>
      )}
      <ProviderCalendar key={shownProviderId} providerId={shownProviderId} />
    </>
  );
};

export default DashboardPage;
